import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { loginApi, getLocationDetails, getStaffLocations } from '../services/api';
import { setOwnerLocationCount } from '../context/OwnerViewContext';
import { SELECTED_BRANCH_DATA, getLogoUrlFromMedia } from '../constants';
import { pickTimeZoneCdFromLocationPayload } from '../utils/locationPayload';
import { isOnlySpecialCharacters } from '../utils/textValidation';
import Phone from './Phone';
import { C } from './theme';
import brisqueLogo from '../assets/BrisqueLogo/png/BrisqueLogo.png';

const useIsDesktop = () => {
    const query = '(min-width: 900px)';
    const getMatches = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
    const [isDesktop, setIsDesktop] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const media = window.matchMedia(query);
        const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
        setIsDesktop(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
};

const EyeIcon = ({ open }: { open: boolean }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: "#6E7380", userSelect: "none", display: "block" }}>
        {open ? (
            <>
                <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            </>
        ) : (
            <>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-5 0-9.27-3.11-11-7a10.17 10.17 0 0 1 2.31-3.65M6.53 6.53A9.96 9.96 0 0 1 12 5c5 0 9.27 3.11 11 7a10.07 10.07 0 0 1-1.3 2.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.73 10.73A2 2 0 0 0 12 14a2 2 0 0 0 1.27-.73M2 2l20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </>
        )}
    </svg>
);

const slides = [
    {
        eyebrow: '',
        headingStart: 'From ',
        headingAccent: 'Counter',
        headingEnd: ' to Cloud.',
        body: "Whether you're starting fresh or scaling your restaurant network, Brisque's experts are ready to help you modernize operations, engage customers, and grow smarter.",
    },
    {
        eyebrow: '',
        headingStart: 'Manage ',
        headingAccent: 'Smarter',
        headingEnd: ' Sale.',
        body: 'Track orders, manage menus, and configure locations all from one powerful admin portal built for modern restaurant operations.',
    },
    {
        eyebrow: '',
        headingStart: 'Make a Customer, ',
        headingAccent: 'Every',
        headingEnd: ' Sale.',
        body: 'Brisque gives your team the tools to deliver exceptional experiences, streamline workflows, and keep customers coming back.',
    },
];

const logoWhiteFilter = 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(103deg) brightness(103%) contrast(101%)';

const getLoginIdentityValidationError = (businessName: string, userId: string): string => {
    if (isOnlySpecialCharacters(businessName)) return 'Business Name cannot contain only special characters.';
    if (isOnlySpecialCharacters(userId)) return 'User ID cannot contain only special characters.';
    return '';
};

const DesktopLoginScreen = () => {
    const [businessName, setBusinessName] = useState('');
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSlide, setActiveSlide] = useState(0);
    const { login } = useAuth();
    const { setSelectedBranch } = useBranch();
    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % slides.length);
        }, 3600);
        return () => clearInterval(interval);
    }, []);

    const handleLogin = async () => {
        if (!businessName.trim() || !userId.trim() || !password.trim()) {
            setError('Please enter your Business Name, User ID and Password.');
            return;
        }

        const identityValidationError = getLoginIdentityValidationError(businessName, userId);
        if (identityValidationError) {
            setError(identityValidationError);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = await loginApi({
                businessName: businessName.trim(),
                userId: userId.trim(),
                password,
            });

            const locationId = data.locationId || data.location_id;
            const token = data.accessToken || data.access_token;

            try {
                const res = await getLocationDetails(locationId, token);
                const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                const branchList = payload.branch || payload.branches || [];
                const currentBranch = Array.isArray(branchList)
                    ? branchList.find((b) => (b.id || b.locationId) === locationId)
                    : null;

                const timeZoneCd =
                    pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(currentBranch);

                const workingHours = payload.workingHours || currentBranch?.workingHours || [];
                if (currentBranch) {
                    const logoUrl = getLogoUrlFromMedia(currentBranch.media || payload.media || []) || currentBranch.logoUrl || '';

                    const branch = {
                        ...currentBranch,
                        id: currentBranch.id || locationId,
                        locationName: currentBranch.locationName || currentBranch.name || 'Current Branch',
                        logoUrl,
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };

                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                } else {
                    const branch = {
                        id: locationId,
                        locationName: data.locationName || data.location_name || 'Current Branch',
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };

                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                }
            } catch (_) {
                const branch = {
                    id: locationId,
                    locationName: data.locationName || data.location_name || 'Current Branch',
                };

                localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                setSelectedBranch(branch);
            }

            const role = data.role || '';
            const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
            const isSuper = SUPER_ROLES.includes(role);

            // Owner dashboard only makes sense with >1 outlet. Read the outlet
            // dropdown count (same source as OwnerViewContext) so single-branch
            // owners go straight to the snapshot instead of bouncing through it.
            // null = unknown (fetch failed) -> fall back to owner-view (it self-redirects).
            let locationCount: number | null = null;
            if (isSuper) {
                try {
                    const staffId = data.id || data.staff_id || '';
                    const res = await getStaffLocations(staffId, token);
                    const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                    const list = Array.isArray(payload) ? payload : payload?.branch ?? payload?.branches ?? [];
                    locationCount = Array.isArray(list) ? list.length : 0;
                } catch {
                    locationCount = null;
                }
            }

            // Seed the shared outlet-count cache BEFORE login() flips auth, so the
            // post-login redirect (homePath in App.tsx) lands on the right page with
            // no snapshot<->owner-view bounce. Non-super users reset to 0 (never owner-view).
            setOwnerLocationCount(isSuper ? locationCount : 0);

            // Must await: login() resolves asynchronously (secure storage write) before
            // isAuthenticated flips true. Navigating before that lets ProtectedRoute see
            // isAuthenticated=false, bounce back to /login, and overwrite sessionStorage's
            // auth_redirect with the wrong destination (or leave a stale one in place).
            await login(data);

            const goOwnerView = isSuper && (locationCount === null || locationCount > 1);
            navigate(goOwnerView ? '/owner-view' : '/reports/snapshot', { replace: true });
        } catch (err: any) {
            setError(err.message || 'Your User ID or Password is Incorrect');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleLogin();
    };

    return (
        <div style={{ minHeight: '100vh', background: '#090A0E', padding: 20, boxSizing: 'border-box', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", WebkitFontSmoothing: 'antialiased' }}>
            <style>{`
                .desktop-login-shell {
                    min-height: calc(100vh - 40px);
                    display: grid;
                    grid-template-columns: minmax(520px, 1.1fr) minmax(460px, 0.9fr);
                    border-radius: 28px;
                    overflow: hidden;
                    background: #FFFFFF;
                    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
                }
                .desktop-login-left {
                    position: relative;
                    padding: 42px 34px 38px;
                    display: flex;
                    flex-direction: column;
                    background:
                        radial-gradient(circle at 24% 60%, rgba(242, 30, 50, 0.18), transparent 34%),
                        radial-gradient(circle at 72% 20%, rgba(242, 30, 50, 0.10), transparent 28%),
                        linear-gradient(135deg, #09090D 0%, #120E12 38%, #0A0B10 100%);
                }
                .desktop-slide-panel {
                    overflow: hidden;
                    max-width: 720px;
                    width: 100%;
                }
                .desktop-slide-track {
                    display: flex;
                    width: 300%;
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    will-change: transform;
                }
                .desktop-slide-item {
                    flex: 0 0 33.333%;
                    width: 33.333%;
                    min-width: 0;
                }
                .desktop-login-right {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #FFFFFF;
                    padding: 40px 32px;
                }
                .desktop-login-input::placeholder {
                    color: #B9BBC3;
                    opacity: 1;
                }
                .desktop-login-input:-webkit-autofill,
                .desktop-login-input:-webkit-autofill:hover,
                .desktop-login-input:-webkit-autofill:focus,
                .desktop-login-input:-webkit-autofill:active {
                    -webkit-text-fill-color: #111111 !important;
                    -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
                    box-shadow: 0 0 0 1000px #FFFFFF inset !important;
                    background-color: #FFFFFF !important;
                    transition: background-color 9999s ease-in-out 0s;
                }
                @media (max-width: 1023px) {
                    .desktop-login-shell {
                        grid-template-columns: 1fr;
                    }
                    .desktop-login-left {
                        min-height: 320px;
                    }
                }
            `}</style>

            <div className="desktop-login-shell">
                <div className="desktop-login-left">
                    <div>
                        <img
                            src={brisqueLogo}
                            alt="Brisque"
                            style={{ width: 180, height: 'auto', filter: logoWhiteFilter }}
                        />
                    </div>

                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <div className="desktop-slide-panel">
                        <div
                            className="desktop-slide-track"
                            style={{ transform: `translateX(-${activeSlide * (100 / 3)}%)` }}
                        >
                            {slides.map((s, index) => (
                                <div key={index} className="desktop-slide-item">
                                    <div style={{ width: 48, height: 4, borderRadius: 999, background: '#F21E32', marginBottom: 28 }} />
                                    <div style={{ fontSize: 40, lineHeight: 1.12, fontWeight: 700, letterSpacing: -1, color: '#F3F4F6', marginBottom: 20 }}>
                                        <span>{s.headingStart}</span>
                                        <span style={{ color: '#F21E32' }}>{s.headingAccent}</span>
                                        <span>{s.headingEnd}</span>
                                    </div>
                                    <div style={{ maxWidth: 560, fontSize: 15, lineHeight: 1.65, color: 'rgba(255,255,255,0.58)' }}>
                                        {s.body}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 34 }}>
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => setActiveSlide(index)}
                                    style={{
                                        width: index === activeSlide ? 26 : 10,
                                        height: 10,
                                        borderRadius: 999,
                                        border: 'none',
                                        background: index === activeSlide ? '#F21E32' : 'rgba(255,255,255,0.20)',
                                        display: 'inline-block',
                                        padding: 0,
                                        cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    </div>

                </div>

                <div className="desktop-login-right">
                    <div style={{ width: '100%', maxWidth: 400 }}>
                        <div style={{ textAlign: 'center', marginBottom: 46 }}>
                            <img
                                src={brisqueLogo}
                                alt="Brisque"
                                style={{ width: 150, height: 'auto', margin: '0 auto 36px', display: 'block' }}
                            />
                        </div>

                        <div style={{ marginBottom: 28 }}>
                            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, color: '#101114', marginBottom: 8 }}>Sign In</div>
                            <div style={{ fontSize: 13, color: '#B4B6BC', lineHeight: 1.6 }}>
                                Please enter your details to access your dashboard.
                            </div>
                        </div>

                        {error && (
                            <div style={{
                                marginBottom: 18,
                                borderRadius: 12,
                                border: '1px solid rgba(220, 38, 38, 0.20)',
                                background: '#FEF2F2',
                                color: '#DC2626',
                                padding: '12px 14px',
                                fontSize: 12,
                                lineHeight: 1.5,
                            }}>
                                {error}
                            </div>
                        )}

                        <div style={{ marginBottom: 18 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#8D8F97', marginBottom: 10 }}>Business Name</div>
                            <input
                                className="desktop-login-input"
                                type="text"
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your business name"
                                style={{
                                    width: '100%',
                                    height: 50,
                                    borderRadius: 14,
                                    border: '1px solid #ECEDEF',
                                    background: '#FFFFFF',
                                    padding: '0 16px',
                                    boxSizing: 'border-box',
                                    fontSize: 14,
                                    color: '#111111',
                                    outline: 'none',
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: 18 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#8D8F97', marginBottom: 10 }}>User ID</div>
                            <input
                                className="desktop-login-input"
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your user ID"
                                style={{
                                    width: '100%',
                                    height: 50,
                                    borderRadius: 14,
                                    border: '1px solid #ECEDEF',
                                    background: '#FFFFFF',
                                    padding: '0 16px',
                                    boxSizing: 'border-box',
                                    fontSize: 14,
                                    color: '#111111',
                                    outline: 'none',
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#8D8F97', marginBottom: 10 }}>Password</div>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="desktop-login-input"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter your password"
                                    style={{
                                        width: '100%',
                                        height: 50,
                                        borderRadius: 14,
                                        border: '1px solid #ECEDEF',
                                        background: '#FFFFFF',
                                        padding: '0 44px 0 16px',
                                        boxSizing: 'border-box',
                                        fontSize: 14,
                                        color: '#111111',
                                        outline: 'none',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((value) => !value)}
                                    style={{
                                        position: 'absolute',
                                        right: 12,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: 28,
                                        height: 28,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <EyeIcon open={showPassword} />
                                </button>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleLogin}
                            disabled={loading}
                            style={{
                                width: '100%',
                                height: 54,
                                borderRadius: 14,
                                border: 'none',
                                background: loading ? '#F25D6B' : '#F21E32',
                                color: '#FFFFFF',
                                fontSize: 14,
                                fontWeight: 700,
                                letterSpacing: 2.2,
                                textTransform: 'uppercase',
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </button>

                        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 14, color: '#B9BBC3' }}>Help Center</div>

                        <div style={{ textAlign: 'center', marginTop: 60 }}>
                            <div style={{ fontSize: 11, color: '#C8CAD0', marginBottom: 8 }}>Powered by</div>
                            <img
                                src={brisqueLogo}
                                alt="Brisque"
                                style={{ width: 84, height: 'auto', opacity: 0.35, display: 'block', margin: '0 auto' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MaghilLogoMobile = () => (
    <div style={{ textAlign: 'center', marginBottom: 52 }}>
        <img
            src={brisqueLogo}
            alt="Brisque"
            style={{ width: '56%', maxWidth: 188, height: 'auto', display: 'block', margin: '0 auto' }}
        />
    </div>
);

const MobileLoginScreen = () => {
    const [businessName, setBusinessName] = useState('');
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const { setSelectedBranch } = useBranch();
    const navigate = useNavigate();

    const handleLogin = async () => {
        if (!businessName.trim() || !userId.trim() || !password.trim()) {
            setError('Please enter your Business Name, User ID and Password.');
            return;
        }

        const identityValidationError = getLoginIdentityValidationError(businessName, userId);
        if (identityValidationError) {
            setError(identityValidationError);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const data = await loginApi({ businessName: businessName.trim(), userId: userId.trim(), password });

            const locationId = data.locationId || data.location_id;
            const token = data.accessToken || data.access_token;
            try {
                const res = await getLocationDetails(locationId, token);
                const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                const branchList = payload.branch || payload.branches || [];
                const currentBranch = Array.isArray(branchList)
                    ? branchList.find((b) => (b.id || b.locationId) === locationId)
                    : null;
                const timeZoneCd =
                    pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(currentBranch);
                const workingHours = payload.workingHours || currentBranch?.workingHours || [];
                if (currentBranch) {
                    const logoUrl = getLogoUrlFromMedia(currentBranch.media || payload.media || []) || currentBranch.logoUrl || '';
                    const branch = {
                        ...currentBranch,
                        id: currentBranch.id || locationId,
                        locationName: currentBranch.locationName || currentBranch.name || 'Current Branch',
                        logoUrl,
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };
                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                } else {
                    const branch = {
                        id: locationId,
                        locationName: data.locationName || data.location_name || 'Current Branch',
                        ...(timeZoneCd ? { timeZoneCd } : {}),
                        workingHours,
                    };
                    localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                    setSelectedBranch(branch);
                }
            } catch (_) {
                const branch = {
                    id: locationId,
                    locationName: data.locationName || data.location_name || 'Current Branch',
                };
                localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branch));
                setSelectedBranch(branch);
            }

            const role = data.role || '';
            const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
            const isSuper = SUPER_ROLES.includes(role);

            // Owner dashboard only makes sense with >1 outlet. Read the outlet
            // dropdown count (same source as OwnerViewContext) so single-branch
            // owners go straight to the snapshot instead of bouncing through it.
            // null = unknown (fetch failed) -> fall back to owner-view (it self-redirects).
            let locationCount: number | null = null;
            if (isSuper) {
                try {
                    const staffId = data.id || data.staff_id || '';
                    const res = await getStaffLocations(staffId, token);
                    const payload = (res as any)?.metaDataInfo?.data ?? (res as any)?.data ?? res;
                    const list = Array.isArray(payload) ? payload : payload?.branch ?? payload?.branches ?? [];
                    locationCount = Array.isArray(list) ? list.length : 0;
                } catch {
                    locationCount = null;
                }
            }

            // Seed the shared outlet-count cache BEFORE login() flips auth, so the
            // post-login redirect (homePath in App.tsx) lands on the right page with
            // no snapshot<->owner-view bounce. Non-super users reset to 0 (never owner-view).
            setOwnerLocationCount(isSuper ? locationCount : 0);

            // Must await: login() resolves asynchronously (secure storage write) before
            // isAuthenticated flips true. Navigating before that lets ProtectedRoute see
            // isAuthenticated=false, bounce back to /login, and overwrite sessionStorage's
            // auth_redirect with the wrong destination (or leave a stale one in place).
            await login(data);

            const goOwnerView = isSuper && (locationCount === null || locationCount > 1);
            navigate(goOwnerView ? '/owner-view' : '/reports/snapshot', { replace: true });
        } catch (err: any) {
            setError(err.message || 'Your User ID or Password is Incorrect');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleLogin();
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', height: 46, borderRadius: 16,
        border: '1px solid #E8E8EB',
        padding: '0 18px', fontSize: 16, fontWeight: 400, color: '#111111',
        background: '#FFFFFF', outline: 'none',
        boxSizing: 'border-box', fontFamily: 'inherit',
        transition: 'border-color 0.2s',
        boxShadow: 'none',
    };

    const labelStyle: React.CSSProperties = {
        fontSize: 11, fontWeight: 700, color: '#8C8C92',
        letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10,
    };

    return (
        <Phone>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#FFFFFF', overflowY: 'auto' }}>
                <style>{`
                    .login-input::placeholder {
                        color: #BFC1C6;
                        opacity: 1;
                    }
                    .login-input:-webkit-autofill,
                    .login-input:-webkit-autofill:hover,
                    .login-input:-webkit-autofill:focus,
                    .login-input:-webkit-autofill:active {
                        -webkit-text-fill-color: #111111 !important;
                        caret-color: #111111;
                        background-color: #FFFFFF !important;
                        background-image: none !important;
                        -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
                        box-shadow: 0 0 0 1000px #FFFFFF inset !important;
                        border-radius: 16px;
                        -webkit-appearance: none;
                        transition: background-color 9999s ease-in-out 0s;
                    }
                `}</style>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '32px 28px 16px' }}>
                    <MaghilLogoMobile />

                    <div style={{ marginBottom: 26 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#111111', marginBottom: 10, lineHeight: 1.2 }}>Sign In</div>
                        <div style={{ fontSize: 14, fontWeight: 400, color: '#B2B3B8', lineHeight: 1.45 }}>
                            Please enter your details to access your dashboard.
                        </div>
                    </div>

                    {error && (
                        <div style={{
                            background: C.redSoft, border: `1px solid ${C.red}33`,
                            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                            fontSize: 12, color: C.red, lineHeight: 1.5,
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ marginBottom: 22 }}>
                        <div style={labelStyle}>Business Name</div>
                        <input
                            className="login-input"
                            type="text"
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter your business name"
                            style={inputStyle}
                        />
                    </div>

                    <div style={{ marginBottom: 22 }}>
                        <div style={labelStyle}>User ID</div>
                        <input
                            className="login-input"
                            type="text"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter your user ID"
                            style={inputStyle}
                        />
                    </div>

                    <div style={{ marginBottom: 34 }}>
                        <div style={labelStyle}>Password</div>
                        <div style={{ position: 'relative' }}>
                            <input
                                className="login-input"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter your password"
                                style={{ ...inputStyle, paddingRight: 48 }}
                            />
                            <div
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute', right: 16, top: '50%',
                                    transform: 'translateY(-50%)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <EyeIcon open={showPassword} />
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleLogin}
                        disabled={loading}
                        style={{
                            width: '100%', height: 58, borderRadius: 18,
                            background: loading ? '#F25D6B' : '#F21E32',
                            border: 'none', color: '#FFFFFF', fontSize: 15, fontWeight: 700,
                            letterSpacing: 2.2, cursor: loading ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit', textTransform: 'uppercase',
                            boxShadow: 'none',
                        }}
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>

                    <div style={{ textAlign: 'center', marginTop: 30 }}>
                        <span style={{ fontSize: 14, fontWeight: 400, color: '#B3B4BA', cursor: 'pointer' }}>Help Center</span>
                    </div>
                </div>

                <div style={{ textAlign: 'center', padding: '10px 28px 20px' }}>
                    <div style={{ fontSize: 11, fontWeight: 400, color: '#C1C2C7', marginBottom: 6 }}>Powered by</div>
                    <img
                        src={brisqueLogo}
                        alt="Brisque"
                        style={{ width: '22%', maxWidth: 78, height: 'auto', display: 'block', margin: '0 auto', opacity: 0.42 }}
                    />
                </div>
            </div>
        </Phone>
    );
};

export default function LoginScreen() {
    const isDesktop = useIsDesktop();
    return isDesktop ? <DesktopLoginScreen /> : <MobileLoginScreen />;
}

